import React, { useState, useEffect } from 'react';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import { Card, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';

import Loading from '../../loading';
import { RootState } from '../../../store';
import { PaginatorFactory } from '../../../factory';
import { CustomerService } from '../../../services/customer.service';
import { CUSTOMER_COLUMNS } from '../../../builders/customers-builders';
import { CustomerListFilters } from './customers-filters';
import { CustomerTypes } from '../../../types';
import NoData from '../../no-data';
import { Label } from '../../label';
import { invitationStatusLabel } from '../../../sanitalizers/instance-status-label';
import { useNavigate } from 'react-router-dom';

export const CustomerList: React.FC = () => {
    const { pagination: { total, items }, loading } = useSelector((state: RootState) => state.customersAdmin);
    const navigate = useNavigate();

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
        page: 0,
        perPage: 25,
    });

    // The applied filters live in state so paging through results keeps them.
    // Previously the page-change effect refetched with `filter: {}`, so moving
    // to page 2 silently dropped whatever had been searched for.
    const [filters, setFilters] = useState<CustomerTypes.Filters>({} as CustomerTypes.Filters);

    // Applying a filter has to go back to the first page — the old code reused
    // the current page number, so filtering while on page 3 asked for page 3 of
    // a smaller result set and the table came back empty.
    const onFilter = async (nextFilters: CustomerTypes.Filters) => {
        setFilters(nextFilters);
        setPagination(pag => ({ ...pag, page: 0 }));
    }

    useEffect(() => {
        CustomerService.fetchMany({ ...pagination, page: pagination.page + 1, filter: { ...filters } });
    }, [pagination?.perPage, pagination?.page, filters]);

    return (
        <>
            <Card sx={{ mt: 2 }}>
                <CustomerListFilters onFilter={onFilter} />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {CUSTOMER_COLUMNS?.map((column) => (
                                    <TableCell key={column.field}>
                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                            {column?.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading?.fetchMany}>
                            <TableBody>
                                {items?.map((customer) => (
                                    <TableRow
                                        key={customer?.id}
                                        sx={{
                                            ":hover": {
                                                backgroundColor: '#F0F0F0',
                                                cursor: 'pointer'
                                            }
                                        }
                                        }
                                        onClick={() => navigate(`/customers/${customer?.id}/edit`)}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {customer?.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {customer?.invitation?.email || '----'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {customer?.city}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {customer?.state}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {customer?.subscription?.inviteCode || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {
                                                customer?.invitation?.status !== CustomerTypes.OnboardingInvitationStatus.DONE && dayjs(customer.invitation?.expiresAt).isBefore(new Date()) ?
                                                    <Label
                                                        variant={'ghost'}
                                                        color={'error'}
                                                    >
                                                        Expired
                                                    </Label> :
                                                    <Label
                                                        variant={'ghost'}
                                                        color={invitationStatusLabel[customer?.invitation?.status!]?.color}
                                                    >
                                                        {invitationStatusLabel[customer?.invitation?.status!]?.label}
                                                    </Label>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {customer?.invitation?.expiresAt ? dayjs(customer.invitation?.expiresAt).format('MM/DD/YYYY hh:mm A') : '----'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {customer?.createdAt ? dayjs(customer.createdAt).format('MM/DD/YYYY hh:mm A') : '----'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>

                    <When condition={loading.fetchMany === false && items?.length < 1}>
                        <NoData
                            buttonText={null}
                            onClick={() => { }}
                            title={'No records found'}
                            description={'Apparently there is no customers registered.'}
                        />
                    </When>

                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) => setPagination(pag => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) => setPagination(pag => ({ ...pag, page: 0, perPage: parseInt(e.target.value, 10) }))}
                />
            </Card>
        </>
    )
}
